import { useState, useCallback, useRef } from 'react';

import { tv, cn } from '@simplywallst/ui-core/next';

import type { ISO2 } from '@/constants/countries/ISO2';

import { getSanitisedUrl } from '../../utils';
import { BrokerageLogo } from './BrokerageLogo';
import { SearchDrawer } from '../../../SearchDrawer';
import { DropdownEmptyState } from '../DropdownEmptyState';

import { styles } from './styles';

const textField = tv({
  extend: styles,
});

type Option = {
  url: string | null;
  countryISO: ISO2 | null;
  id: string;
  label: string;
  name: string;
};

interface Props {
  options: Option[];
  disabled: boolean;
  onSelect?: (id: string) => void;
  onFocus?: () => void;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  status: 'pending' | 'success' | 'error';
}

const placeholder = 'Search 2,000+ brokers worldwide';

export const BrokerageSearchDrawer = ({
  options,
  disabled,
  onSelect,
  onFocus,
  inputValue,
  onInputChange,
  status,
}: Props) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleSelectionChange = useCallback(
    (keys) => {
      setOpen(false);
      onSelect?.(String(...keys));
    },
    [onSelect, setOpen]
  );

  const handleInputChange = useCallback(
    (value?: string) => {
      onInputChange?.(value ?? '');
    },
    [onInputChange]
  );

  const styles = textField({
    isDisabled: disabled,
  });

  const inputEl = cn([
    Number(inputValue?.length) > 0
      ? styles.inputEl({ className: 'text-left' })
      : styles.inputEl({ className: 'text-left opacity-40' }),
  ]);

  return (
    <>
      <fieldset className={styles.fieldset()}>
        <div className={styles.input()}>
          <button
            className={inputEl}
            disabled={disabled}
            onClick={() => setOpen(true)}
            data-cy-id="brokerage-show-suggestions"
            aria-label="Show suggestions"
          >
            {Number(inputValue?.length) > 0 ? inputValue : placeholder}
          </button>
        </div>
      </fieldset>

      {open && (
        <div ref={containerRef} data-theme="light">
          <SearchDrawer
            label="Select a broker"
            placeholder={placeholder}
            items={options}
            inputValue={inputValue}
            onCancel={() => setOpen(false)}
            onFocus={onFocus}
            onSelectionChange={handleSelectionChange}
            onInputChange={handleInputChange}
            emptyStateRender={() => {
              return <DropdownEmptyState close={() => setOpen(false)} />;
            }}
            status={status}
            containerRef={containerRef}
          >
            {(item) => (
              <SearchDrawer.Option
                textValue={item.label}
                key={item.id}
                className="p-2"
              >
                <div className="ml-2">
                  <div className="grid grid-cols-[40px_1fr] grid-rows-[40px] items-center gap-2">
                    <BrokerageLogo
                      name={item.name}
                      url={getSanitisedUrl(item.url)}
                    />
                    <div className="max-w-[70vw] sm:max-w-[80vw] md:max-w-[90vw]">
                      <p className="m-0 truncate text-xs">{item.label}</p>
                      {item.url !== null ? (
                        <p className="text-inherit m-0 text-tiny opacity-50">
                          {getSanitisedUrl(item.url)}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </SearchDrawer.Option>
            )}
          </SearchDrawer>
        </div>
      )}
    </>
  );
};
